import { formatAmounts as format, pluralize } from '@aytm/app/stats/lib/util/helpers'

const formatAmounts = obj => format(obj, 'amount', 1)

export const getAnswer = ({ answer, answerVariants, viewMode, priceTemplate, trait, traitName, traitValues }) => {
  const hcAnswers = { total: answer?.total || 0 }

  if (viewMode === 'totalPickedUp') {
    return Object.assign(
      {},
      hcAnswers,
      getTotalPickedUpAnswer({ answer, answerVariants, trait, traitName, traitValues })
    )
  }

  for (const [ak, av] of Object.entries(answer || {})) {
    if (ak[0] !== 'a' || Number.isNaN(Number(ak.slice(1)))) continue

    const answerValue = {}
    hcAnswers[ak] = answerValue

    answerValue.amount = av[viewMode] || 0

    if (['pAmount', 'pVolume'].includes(viewMode)) {
      answerValue.amount = answerValue.amount.toFixed(2)
    } else {
      answerValue.amount = formatAmounts(answerValue)
    }

    if (viewMode === 'amount') answerValue.percents = av.percents || 0

    // `amount` is a formatted string after formatAmounts/toFixed — coerce so the
    // singular/plural choice compares numbers (loose-eq parity with legacy).
    answerValue.unitLabel = ['attention', 'time', 'timeToFirstClick'].includes(viewMode)
      ? pluralize(Number(answerValue.amount), 'second', 'seconds')
      : viewMode === 'amount'
        ? pluralize(Number(answerValue.amount), 'respondent', 'respondents')
        : ' '

    if (viewMode === 'pAmount' && priceTemplate) {
      answerValue.unitTemplate = priceTemplate.replace(/{{price}}/gi, '{{unit}}')
    }

    if (viewMode === 'amount' && av.errLO && av.errHI) {
      answerValue.errLO = av.errLO
      answerValue.errHI = av.errHI
    }

    if (trait) {
      const answerTrait = {}
      answerValue[traitName] = answerTrait

      const avTrait = av[traitName]
      for (const [tk, tv] of Object.entries(avTrait || {})) {
        const answerTraitValue = {}
        answerTrait[tk] = answerTraitValue

        answerTraitValue.amount = tv[viewMode] || 0
        if (viewMode === 'amount') answerTraitValue.percents = tv.percents || 0
        answerTraitValue.unitLabel = answerValue.unitLabel

        if (['pAmount', 'pVolume'].includes(viewMode)) {
          answerTraitValue.amount = answerTraitValue.amount.toFixed(2)
        } else {
          answerTraitValue.amount = formatAmounts(answerTraitValue)
        }

        if (viewMode === 'pAmount' && priceTemplate) {
          answerTraitValue.unitTemplate = priceTemplate.replace(/{{price}}/gi, '{{unit}}')
        }
      }
    }
  }

  return hcAnswers
}

// totalNoAction fallback below relies on `||` having lower precedence than `-`:
//   `traitAnswer.totalNoAction || X.total - purchased - totalPickedUp`
// parses as `… || ((X.total - purchased) - totalPickedUp)`. Don't add parens
// around the LHS — it'd flip the meaning.
const getTotalPickedUpAnswer = ({ answer, answerVariants, trait, traitName, traitValues }) => {
  const total = answer?.total || 0
  const hcAnswers = {}

  for (const av of answerVariants || []) {
    const position = av.position
    const ak = `a${position}`
    const bk = `b${position}`

    const akAnswer = answer?.[ak] || {}
    const purchasedPercents = akAnswer.percents || 0
    const pickedUpPercents = akAnswer.pickedUpPercents || 0
    const bAnswer = {
      amount: purchasedPercents + pickedUpPercents,
      total
    }

    if (trait) {
      const avTrait = akAnswer[traitName]

      traitValues.forEach((traitValue, idx) => {
        const traitAnswer = avTrait[traitValue.id] || {}

        const purchasedAmount = traitAnswer.amount || 0
        const purchasedTraitPercents = (purchasedAmount / total) * 100
        const purchaseKey = `a${idx * 3 + 1}`
        bAnswer[purchaseKey] = {
          amount: purchasedAmount,
          percents: purchasedTraitPercents
        }

        const totalPickedUp = traitAnswer.totalPickedUp || 0
        const pickedUpTraitPercents = traitAnswer.pickedUpPercents || (totalPickedUp / total) * 100
        const pickedUpKey = `a${idx * 3 + 2}`
        bAnswer[pickedUpKey] = {
          amount: totalPickedUp,
          percents: pickedUpTraitPercents
        }

        const totalNoAction =
          traitAnswer.totalNoAction || answer[traitName]?.[traitValue.id]?.total - purchasedAmount - totalPickedUp
        const noActionPercents = traitAnswer.noActionPercents || (totalNoAction / total) * 100
        const noActionKey = `a${idx * 3 + 3}`
        bAnswer[noActionKey] = {
          amount: totalNoAction,
          percents: noActionPercents
        }
      })
    } else {
      const purchasedAmount = akAnswer.amount || 0
      const totalPickedUp = akAnswer.totalPickedUp || 0
      const totalNoAction = akAnswer.totalNoAction || 0
      const noActionPercents = akAnswer.noActionPercents || 0

      bAnswer.a1 = { amount: purchasedAmount, percents: purchasedPercents }
      bAnswer.a2 = { amount: totalPickedUp, percents: pickedUpPercents }
      bAnswer.a3 = { amount: totalNoAction, percents: noActionPercents }
    }

    hcAnswers[bk] = bAnswer
  }

  return hcAnswers
}
